import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpService } from '../service/http.service';
import { SchoolInvoiceService } from '../school-invoice.service';
import * as moment from 'moment';
import { DecimalValidator } from '../validator/decimal-validator';
import { BookingService } from '../service/booking.service';
import { ToastMessageService } from '../toast-message.service';
import { PaymentOrder } from '../payment-order';
import { SchoolInvoice } from '../school-invoice';
import { NumberValidator } from '../validator/number-validator';
import { InvoiceFormValidator } from '../validator/invoice-form-validator';
import { CurrencyService } from '../service/currency.service';
import { BookingTransactionsService } from '../service/booking-transactions.service';
import { ConfirmationService } from 'primeng/api';
import Decimal from 'decimal.js';
import { UserService } from '../service/user.service';
import { FileUpload } from 'primeng/fileupload';

@Component({
  selector: 'app-edit-school-invoice',
  templateUrl: './edit-school-invoice.component.html',
  styleUrls: ['./edit-school-invoice.component.css']
})
export class EditSchoolInvoiceComponent implements OnInit {

  invoiceForm!: FormGroup;
  paymentOrderForms: FormGroup[] = [];
  submitFail = false;
  invalidAmount = false;
  isSchoolInvoiceValidator: boolean = false;
  selectedFile: File | null = null;

  @Output() newInvoiceEvent = new EventEmitter<SchoolInvoice | null>();
  @ViewChild("fileUploader") fileUploader: FileUpload | null = null;

  constructor(
    private httpService: HttpService,
    public schoolInvoiceService: SchoolInvoiceService,
    private builder: FormBuilder,
    public bookingService: BookingService,
    private toastService: ToastMessageService,
    public currencyService: CurrencyService,
    private bookingTransactionService: BookingTransactionsService,
    private confirmationService: ConfirmationService,
    private userService: UserService
  ) {
    if(userService.hasAnyRole(["superadmin", "school_invoice_validator"])){
      this.isSchoolInvoiceValidator = true;
    }
   }

  ngOnInit(): void {
    this.httpService.getSchoolInvoiceWithPaymentOrdersByInvoiceId(this.schoolInvoiceService.selectedInvoiceId!).subscribe(res => {
      this.schoolInvoiceService.invoiceWithPaymentOrders = res;
      let invoice = this.schoolInvoiceService.invoiceWithPaymentOrders;
      this.invoiceForm = this.builder.group({
        invoiceNumber: invoice.invoiceNumber,
        amount: [invoice.amount.toString(), Validators.compose([DecimalValidator, NumberValidator])],
        currency: invoice.currency,
        date: [moment(invoice.date).toDate(), Validators.required],
        dueDate: invoice.dueDate==null?null:moment(invoice.dueDate).toDate(),
        paymentType: invoice.paymentType==null?this.bookingService.paymentTypes[0]:invoice.paymentType,
        comment: invoice.comment,
        multiplePayments: invoice.paymentOrders.length>1,
        isValid: invoice.isValid
      }, {
        validators: InvoiceFormValidator
      });
      //if the invoice has more than 2 payment orders, then multiplePayments is true
      if(invoice.paymentOrders.length > 1){
        this.invoiceForm.get("multiplePayments")!.setValue(true);
      } else {
        this.invoiceForm.get("multiplePayments")!.setValue(false);
      }
      this.initPaymentOrders();
    });
  }

  initPaymentOrders(){
    let invoice = this.schoolInvoiceService.invoiceWithPaymentOrders!;
    for(let order of invoice.paymentOrders){
      this.paymentOrderForms.push(this.builder.group({
        amount: [order.amount.toString(), Validators.compose([DecimalValidator, NumberValidator])],
        dueDate: moment(order.dueDate).toDate(),
        id: order.id
      }));
    }
  }

  addPaymentOrder(){
    this.paymentOrderForms.push(this.builder.group({
      amount: [0, Validators.compose([DecimalValidator, NumberValidator])],
      dueDate: [null, Validators.required],
      id: null
    }));
  }

  deletePaymentOrderForm(i: number){
    this.paymentOrderForms.splice(i, 1);
  }

  onFileSelect(files: File[]){
    if(files.length > 0){
      this.selectedFile = files[0];
    }
  }
  clearSelectedFile(){
    this.fileUploader?.clear();
    this.selectedFile = null;
  }

  submit(){
    this.submitFail = false;
    if(this.invoiceForm.invalid){
      this.toastService.addError("Error", "Form is not completed correctly.");
      this.submitFail = true;
      return;
    }
    let paymentTypeId = this.bookingTransactionService.getPaymentReceivedType();
    let paymentType = this.invoiceForm.value.paymentType;
    if(paymentTypeId != null && paymentType.id != paymentTypeId){
      this.confirmationService.confirm({
        message: "Means of payment: \"" + paymentType.name + "\" does not match the means of payment of received payments from customer. Are you sure to update the invoice with this configuration?",
        accept: () => {
          this.putSchoolInvoice();
        },
        reject: () => {
          this.submitFail = true;
          return;
        }
      });
    } else {
      this.putSchoolInvoice();
    }
  }

  toUSFormatAmount(amountStr: string){
    return amountStr.replace(",", ".");
  }

  putSchoolInvoice(){
    const amountStr = this.toUSFormatAmount(this.invoiceForm.value.amount);
    if(!this.invoiceForm.value.multiplePayments){
      let invoice = this.schoolInvoiceService.invoiceWithPaymentOrders!;
      invoice.invoiceNumber = this.invoiceForm.value.invoiceNumber;
      invoice.date = moment(this.invoiceForm.value.date).utc(true);
      invoice.amount = new Decimal(amountStr);
      invoice.currency = this.invoiceForm.value.currency;
      invoice.paymentType = this.invoiceForm.value.paymentType;
      invoice.comment = this.invoiceForm.value.comment;
      invoice.dueDate = moment(this.invoiceForm.value.dueDate).utc(true);
      invoice.isValid = this.invoiceForm.value.isValid;
      this.httpService.putSchoolInvoiceWithOnePaymentOrder(invoice, this.selectedFile).subscribe(() => {
        this.toastService.addSuccess("Success", "Invoice updated");
        this.newInvoiceEvent.emit(null);
      }, error=> {
        this.submitFail = true;
        this.toastService.addError("Error", error.message);
      });
    } else {
      if(!this.schoolInvoiceService.checkPaymentAmounts(this.invoiceForm, this.paymentOrderForms)){
        this.invalidAmount = true;
        return;
      } else {
        this.invalidAmount = false;
      }
      let paymentOrders: PaymentOrder[] = [];
      let invoice = this.schoolInvoiceService.invoiceWithPaymentOrders!;
      invoice.invoiceNumber = this.invoiceForm.value.invoiceNumber;
      invoice.date = moment(this.invoiceForm.value.date).utc(true);
      invoice.amount = new Decimal(amountStr);
      invoice.currency = this.invoiceForm.value.currency;
      invoice.paymentType = this.invoiceForm.value.paymentType;
      invoice.comment = this.invoiceForm.value.comment;
      invoice.dueDate = null;
      invoice.isValid = this.invoiceForm.value.isValid;
        for(let order of this.paymentOrderForms){
          if(order.valid){
            paymentOrders.push(new PaymentOrder(
              order.value.amount,
              null,
              moment(order.value.dueDate).utc(true),
              invoice.currency,
              order.value.id
            ));
          } else {
            this.toastService.addError("Error", "Form value is invalid");
            throw new Error("there is invalid order form.");
          }
        }
        invoice.paymentOrders = paymentOrders;
        this.httpService.putSchoolInvoiceWithPaymentOrders(invoice, this.selectedFile).subscribe(() => {
          this.toastService.addSuccess("Success", "Invoice successfully updated.");
          this.newInvoiceEvent.emit(null);
        }, error => {
          this.submitFail = true;
          this.toastService.addError("Error", error.message);
        });
    }


  }

}
